import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Horizon } from '../components/Horizon';
import { StatusGlyph } from '../components/StatusGlyph';
import { api, type Roster } from '../lib/api';
import { DRIVER_CLASS_LABEL, formatDate, formatDue, fullName, plural } from '../lib/format';
import './Fleet.css';

type Filter = 'all' | 'blocked' | 'expiring' | 'pending';

export function Fleet() {
  const [filter, setFilter] = useState<Filter>('all');
  const [query, setQuery] = useState('');
  const [includeTerminated, setIncludeTerminated] = useState(false);

  const { data, isPending, error } = useQuery({
    queryKey: ['roster', includeTerminated],
    queryFn: () => api.get<Roster>(`/api/drivers?includeTerminated=${includeTerminated}`),
  });

  const visible = useMemo(() => {
    if (!data) return [];
    const needle = query.trim().toLowerCase();
    return data.drivers.filter((d) => {
      // Same test as the ledger number and the Horizon gutter.
      if (filter === 'blocked' && d.compliant) return false;
      if (filter === 'expiring' && d.counts.expiringSoon === 0) return false;
      if (filter === 'pending' && d.counts.pendingReview === 0) return false;
      if (needle) {
        const haystack = `${d.firstName} ${d.lastName} ${d.employeeId ?? ''}`.toLowerCase();
        if (!haystack.includes(needle)) return false;
      }
      return true;
    });
  }, [data, filter, query]);

  if (isPending) return <p className="muted">Loading the fleet…</p>;
  if (error) return <p className="field-error">{(error as Error).message}</p>;
  if (!data) return null;

  const t = data.totals;
  // Distinct drivers, from the API. Summing withExpired and withMissing counts a driver
  // who has both twice.
  const blocked = t.nonCompliant;

  return (
    <div className="fleet">
      <header className="fleet-head">
        <p className="eyebrow">Driver qualification files · as of {formatDate(data.asOf)}</p>
        <h1 className="fleet-title">Fleet</h1>

        {/*
          A sentence, not a row of stat cards.
          The first number is the one that carries legal consequence, so it is said in
          those words rather than labelled "non-compliant" — a manager should not have to
          translate a dashboard metric into what it means for a driver in a truck.
        */}
        <p className="ledger">
          {t.drivers === 0 ? (
            <>No drivers on file yet.</>
          ) : (
            <>
              <FilterNumber
                n={blocked}
                tone={blocked > 0 ? 'expired' : 'ok'}
                onClick={() => setFilter(blocked > 0 ? 'blocked' : 'all')}
                active={filter === 'blocked'}
              />{' '}
              {blocked === 1 ? 'driver has a file that' : 'drivers have files that'} would not survive
              an audit today.{' '}
              <FilterNumber
                n={t.expiringSoon}
                tone="expiring"
                onClick={() => setFilter('expiring')}
                active={filter === 'expiring'}
              />{' '}
              {t.expiringSoon === 1 ? 'lapses' : 'lapse'} within 60 days.{' '}
              <FilterNumber
                n={t.pendingReview}
                tone="pending"
                onClick={() => setFilter('pending')}
                active={filter === 'pending'}
              />{' '}
              {plural(t.pendingReview, 'upload')} {t.pendingReview === 1 ? 'is' : 'are'} waiting on
              you.
            </>
          )}
        </p>

        {filter !== 'all' && (
          <button type="button" className="btn btn-secondary btn-sm" onClick={() => setFilter('all')}>
            Clear filter
          </button>
        )}
      </header>

      {t.drivers > 0 && <Horizon drivers={data.drivers} asOf={data.asOf} />}

      <section className="card fleet-table-card">
        <div className="card-header">
          <h2 className="fleet-table-title">
            {filter === 'all' ? 'All drivers' : 'Filtered'}{' '}
            <span className="muted tnum">({visible.length})</span>
          </h2>
          <div className="fleet-controls">
            <label className="sr-only" htmlFor="fleet-search">
              Search drivers
            </label>
            <input
              id="fleet-search"
              type="text"
              placeholder="Name or employee ID"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <label className="fleet-toggle">
              <input
                type="checkbox"
                checked={includeTerminated}
                onChange={(e) => setIncludeTerminated(e.target.checked)}
              />
              Show former drivers
            </label>
          </div>
        </div>

        {visible.length === 0 ? (
          <p className="fleet-empty muted">
            {t.drivers === 0
              ? 'Add your first driver to start tracking qualification files.'
              : 'No drivers match this filter.'}
          </p>
        ) : (
          <div className="scroll-x">
            <table>
              <thead>
                <tr>
                  <th scope="col">Driver</th>
                  <th scope="col">Class</th>
                  <th scope="col">Needs attention</th>
                  <th scope="col">Next deadline</th>
                  <th scope="col" className="num">
                    On file
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((d) => (
                  <tr key={d.id}>
                    <td>
                      <Link to={`/drivers/${d.id}`} className="driver-link">
                        {fullName(d)}
                      </Link>
                      {d.employeeId && <span className="driver-id cite">{d.employeeId}</span>}
                      {d.status === 'terminated' && <span className="former">former</span>}
                    </td>
                    <td>
                      <span className="class-tag">{DRIVER_CLASS_LABEL[d.driverClass]}</span>
                    </td>
                    <td>
                      {d.headline ? (
                        <div className="headline">
                          <StatusGlyph status={d.headline.status} />
                          <span className="headline-title">{d.headline.title}</span>
                          <span className="cite">{d.headline.cfrCite}</span>
                        </div>
                      ) : (
                        <StatusGlyph status="satisfied" />
                      )}
                    </td>
                    <td className="tnum">
                      {d.nextDueAt ? (
                        <>
                          {formatDate(d.nextDueAt)}
                          <span className="due-rel muted">
                            {formatDue(
                              Math.round(
                                (Date.parse(`${d.nextDueAt.slice(0, 10)}T00:00:00Z`) -
                                  Date.parse(`${data.asOf}T00:00:00Z`)) /
                                  86_400_000,
                              ),
                            )}
                          </span>
                        </>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="num tnum">
                      {d.counts.satisfied}
                      <span className="muted">
                        /{d.counts.satisfied + d.counts.expired + d.counts.missing + d.counts.expiringSoon + d.counts.pendingReview}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

/** A number in the ledger sentence that doubles as a filter control. */
function FilterNumber({
  n,
  tone,
  onClick,
  active,
}: {
  n: number;
  tone: 'expired' | 'expiring' | 'pending' | 'ok';
  onClick: () => void;
  active: boolean;
}) {
  if (n === 0) {
    return <span className="ledger-num is-zero tnum">No</span>;
  }
  return (
    <button
      type="button"
      className={`ledger-num tone-${tone}${active ? ' is-active' : ''} tnum`}
      onClick={onClick}
    >
      {n}
    </button>
  );
}
